import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Plus, X, Edit3, Trash2, Sparkles, Clock, Share2, Network, GitFork } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const EMPTY_STEP = () => ({ _key: Math.random().toString(36).slice(2, 9), technique: "", target: "", duration_minutes: 10, notes: "" });
const EMPTY_PROTOCOL = { name: "", description: "", steps: [EMPTY_STEP()], focus_muscles: [], tags: [] };

export default function Protocols() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/protocols");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      const payload = {
        name: p.name,
        description: p.description || "",
        steps: (p.steps || []).filter((s) => s.technique?.trim()).map((s) => {
          const { _key: _unused, ...rest } = s;
          return rest;
        }),
        focus_muscles: p.focus_muscles || [],
        tags: p.tags || [],
      };
      if (p.id) await api.patch(`/protocols/${p.id}`, payload);
      else await api.post("/protocols", payload);
      toast.success(p.id ? "Protocol updated" : "Protocol added");
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this protocol?")) return;
    await api.delete(`/protocols/${id}`);
    toast.success("Deleted");
    load();
  };

  const publish = async (p) => {
    if (!window.confirm(p.community_published_id
      ? "Publish an updated version to the Community? This creates a new fork attributing the original lineage."
      : "Publish this protocol anonymously to the Community library? Your community handle (not your name) will be shown."
    )) return;
    try {
      const payload = {
        name: p.name,
        description: p.description || "",
        steps: (p.steps || []).map((s) => ({ technique: s.technique, target: s.target || "", duration_minutes: s.duration_minutes || null, notes: s.notes || "" })),
        focus_muscles: p.focus_muscles || [],
        modalities: [],
        complaint_tags: p.tags || [],
        parent_protocol_id: p.community_origin_id || p.community_published_id || null,
        source_protocol_id: p.id,
      };
      const r = await api.post("/community/protocols", payload);
      toast.success("Published to community!");
      // Open the detail page
      window.open(`/community/protocols/${r.data.id}`, "_self");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish.");
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2">Treatment library</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">Protocols</h1>
          <p className="text-ink-700 mt-3 max-w-2xl">Reusable treatment sequences. Apply them to sessions and watch the Insights surface which ones deliver the best outcomes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/community"
            data-testid="view-community-btn"
            className="min-h-[48px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-900 font-medium inline-flex items-center gap-2 transition-all"
          >
            <Network className="w-4 h-4" /> Community
          </Link>
          <button
            data-testid="add-protocol-btn"
            onClick={() => setEditing({ ...EMPTY_PROTOCOL })}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all shadow-sm hover:shadow-md"
          >
            <Plus className="w-4 h-4" /> New protocol
          </button>
        </div>
      </div>

      {(() => {
        if (loading) return <div className="text-ink-500">Loading…</div>;
        if (items.length === 0) {
          return (
            <div data-testid="protocols-empty" className="bg-white border border-paper-200 rounded-3xl p-12 text-center">
              <div className="font-heading text-xl text-ink-900 mb-2">No protocols yet</div>
              <p className="text-ink-700 mb-6 max-w-md mx-auto">During a session, after the AI posture analysis, use the "Suggest protocol" button to have Claude propose one tailored to the findings — then save it here.</p>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-paper-200 rounded-2xl p-6 hover:border-sage-500/40 hover:shadow-md transition-all" data-testid={`protocol-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="font-heading text-lg font-medium text-ink-900 flex-1 min-w-0">{p.name}</div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <button onClick={() => publish(p)} className="w-8 h-8 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500" data-testid={`share-protocol-${p.id}`} title="Publish to community">
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditing(p)} className="w-8 h-8 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500" data-testid={`edit-protocol-${p.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(p.id)} className="w-8 h-8 rounded-full hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500" data-testid={`delete-protocol-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {p.community_origin_id && (
                  <Link to={`/community/protocols/${p.community_origin_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100 mb-2 hover:bg-sage-100" data-testid={`origin-badge-${p.id}`}>
                    <GitFork className="w-3 h-3" /> from {p.community_origin_author || "community"}
                  </Link>
                )}
                {p.community_published_id && !p.community_origin_id && (
                  <Link to={`/community/protocols/${p.community_published_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-terracotta-50 text-terracotta-700 border border-terracotta-100 mb-2 hover:bg-terracotta-100" data-testid={`published-badge-${p.id}`}>
                    <Share2 className="w-3 h-3" /> published
                  </Link>
                )}
                {p.description && <p className="text-sm text-ink-700 mb-4 line-clamp-2">{p.description}</p>}
                <div className="space-y-1.5 mb-4">
                  {(p.steps || []).slice(0, 3).map((s, i) => (
                    <div key={`${p.id}-step-${i}`} className="text-xs text-ink-700 flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-paper-100 flex items-center justify-center text-[10px] shrink-0">{i + 1}</span>
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-ink-900">{s.technique}</span>
                        {s.target && <span className="text-ink-500"> · {s.target}</span>}
                        {s.duration_minutes && <span className="text-ink-500"> · {s.duration_minutes}m</span>}
                      </span>
                    </div>
                  ))}
                  {(p.steps || []).length > 3 && (
                    <div className="text-xs text-ink-500 pl-7">+{p.steps.length - 3} more</div>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-ink-500 pt-3 border-t border-paper-200">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {(p.steps || []).reduce((acc, s) => acc + (s.duration_minutes || 0), 0)} min</span>
                  <span>{p.use_count || 0} {p.use_count === 1 ? "use" : "uses"}</span>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {editing && <ProtocolEditor protocol={editing} onClose={() => setEditing(null)} onSave={save} />}
    </Layout>
  );
}

function ProtocolEditor({ protocol, onClose, onSave }) {
  const [p, setP] = useState(() => ({
    ...EMPTY_PROTOCOL,
    ...protocol,
    steps: protocol.steps?.length
      ? protocol.steps.map((s) => ({ _key: Math.random().toString(36).slice(2, 9), ...s }))
      : [EMPTY_STEP()],
  }));

  const updateStep = (i, patch) => {
    const next = [...p.steps];
    next[i] = { ...next[i], ...patch };
    setP({ ...p, steps: next });
  };
  const addStep = () => setP({ ...p, steps: [...p.steps, EMPTY_STEP()] });
  const removeStep = (i) => setP({ ...p, steps: p.steps.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="protocol-editor">
      <div className="bg-white rounded-3xl w-full max-w-2xl p-6 sm:p-8 my-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center" data-testid="close-editor">
          <X className="w-4 h-4" />
        </button>
        <div className="label-overline mb-2">{p.id ? "Edit protocol" : "New protocol"}</div>
        <h2 className="font-heading text-2xl font-medium text-ink-900 mb-6">Define the sequence</h2>

        <div className="space-y-4">
          <div>
            <label className="label-overline block mb-2">Name</label>
            <input
              data-testid="protocol-name"
              value={p.name}
              onChange={(e) => setP({ ...p, name: e.target.value })}
              className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              placeholder="Forward-Head Correction Protocol"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">Rationale</label>
            <textarea
              data-testid="protocol-description"
              rows={2}
              value={p.description}
              onChange={(e) => setP({ ...p, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-none"
              placeholder="When to apply and why."
            />
          </div>

          <div>
            <div className="label-overline block mb-2">Steps</div>
            <div className="space-y-3">
              {p.steps.map((s, i) => (
                <div key={s._key} className="bg-paper-50 rounded-xl p-3 border border-paper-200" data-testid={`step-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-sage-500 text-white text-xs flex items-center justify-center">{i + 1}</span>
                    <input
                      data-testid={`step-technique-${i}`}
                      value={s.technique}
                      onChange={(e) => updateStep(i, { technique: e.target.value })}
                      className="flex-1 min-h-[40px] px-3 rounded-lg bg-white border border-paper-200 outline-none focus:border-sage-500"
                      placeholder="Technique (e.g., Myofascial release)"
                    />
                    <button onClick={() => removeStep(i)} className="w-8 h-8 rounded-full hover:bg-paper-200 flex items-center justify-center text-ink-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input
                      data-testid={`step-target-${i}`}
                      value={s.target}
                      onChange={(e) => updateStep(i, { target: e.target.value })}
                      className="min-h-[40px] px-3 rounded-lg bg-white border border-paper-200 outline-none focus:border-sage-500 text-sm sm:col-span-2"
                      placeholder="Target (e.g., Upper trap)"
                    />
                    <input
                      data-testid={`step-duration-${i}`}
                      type="number"
                      value={s.duration_minutes || ""}
                      onChange={(e) => updateStep(i, { duration_minutes: parseInt(e.target.value) || null })}
                      className="min-h-[40px] px-3 rounded-lg bg-white border border-paper-200 outline-none focus:border-sage-500 text-sm"
                      placeholder="Min"
                    />
                  </div>
                  <input
                    data-testid={`step-notes-${i}`}
                    value={s.notes || ""}
                    onChange={(e) => updateStep(i, { notes: e.target.value })}
                    className="w-full mt-2 min-h-[36px] px-3 rounded-lg bg-white border border-paper-200 outline-none focus:border-sage-500 text-sm"
                    placeholder="Notes (optional)"
                  />
                </div>
              ))}
            </div>
            <button onClick={addStep} className="mt-3 min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-2" data-testid="add-step">
              <Plus className="w-3.5 h-3.5" /> Add step
            </button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button onClick={onClose} className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100" data-testid="cancel-editor">Cancel</button>
          <button data-testid="save-protocol-btn" onClick={() => onSave(p)} disabled={!p.name.trim()} className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium disabled:opacity-50">Save protocol</button>
        </div>
      </div>
    </div>
  );
}

export { ProtocolEditor };
